/**
 * AddLeadModal — shared "Add Lead" modal for manually creating a lead
 * (walk-in, phone call, or off-platform inquiry).
 *
 * Extracted from ListingDetailPage.tsx's inline definition so it can be reused
 * by two entry points:
 *  - ListingDetailPage (Pipeline Board): `listingId` is fixed, no picker shown.
 *  - LeadsIndexPage: no listing is known ahead of time, so a required
 *    listing-picker combobox renders above the email field, sourced from the
 *    `listings` prop. The rest of the form (and the submit button) stay
 *    disabled until a listing is picked.
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { createLead } from '../../../lib/leasing.api';

interface AddLeadModalProps {
  listingId?: string;
  listings?: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddLeadModal({ listingId, listings = [], onClose, onSuccess }: AddLeadModalProps) {
  const needsListingPicker = !listingId;

  const [selectedListingId, setSelectedListingId] = useState('');
  const [listingSearch, setListingSearch] = useState('');
  const [listingDropdownOpen, setListingDropdownOpen] = useState(false);
  const listingComboboxRef = useRef<HTMLDivElement>(null);

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const listingSearchInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const effectiveListingId = listingId ?? selectedListingId;
  const listingPicked = Boolean(effectiveListingId);

  useEffect(() => {
    if (needsListingPicker) {
      listingSearchInputRef.current?.focus();
    } else {
      emailInputRef.current?.focus();
    }
    // Only run on mount — focus behavior should not re-fire on prop identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape-to-close + basic focus trap.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close listing dropdown on outside click
  useEffect(() => {
    if (!needsListingPicker) return;
    function handleClickOutside(e: MouseEvent) {
      if (listingComboboxRef.current && !listingComboboxRef.current.contains(e.target as Node)) {
        setListingDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [needsListingPicker]);

  const filteredListings = listingSearch
    ? listings.filter((l) => l.name.toLowerCase().includes(listingSearch.toLowerCase()))
    : listings;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !effectiveListingId) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await createLead(effectiveListingId, {
        prospectEmail: email.trim(),
        prospectName: name.trim() || null,
        prospectPhone: phone.trim() || null,
        source: source.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      const error = err as Error & { status?: number };
      if (error.status === 409 || (error.message && error.message.includes('LEAD_ALREADY_EXISTS'))) {
        setSubmitError('A lead with this email already exists for this listing.');
      } else {
        setSubmitError(err instanceof Error ? err.message : 'Failed to create lead');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring min-h-[44px]';

  const restDisabled = isSubmitting || (needsListingPicker && !listingPicked);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="add-lead-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-lead-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg">
        <h2 id="add-lead-modal-title" className="text-lg font-semibold mb-1">
          Add Lead
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Manually create a lead for a walk-in, phone call, or off-platform inquiry.
        </p>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          {needsListingPicker && (
            <div ref={listingComboboxRef} className="relative" data-testid="add-lead-listing-picker">
              <label htmlFor="add-lead-listing-search" className="block text-sm font-medium mb-1">
                Listing <span className="text-destructive" aria-hidden="true">*</span>
              </label>
              <input
                ref={listingSearchInputRef}
                id="add-lead-listing-search"
                data-testid="add-lead-listing-search"
                type="text"
                value={listingSearch}
                onFocus={() => setListingDropdownOpen(true)}
                onChange={(e) => {
                  setListingSearch(e.target.value);
                  setSelectedListingId('');
                  setListingDropdownOpen(true);
                }}
                required
                aria-required="true"
                placeholder="Search for a listing…"
                autoComplete="off"
                className={inputCls}
                disabled={isSubmitting}
                aria-expanded={listingDropdownOpen}
                aria-haspopup="listbox"
              />
              {listingDropdownOpen && filteredListings.length > 0 && (
                <ul
                  role="listbox"
                  className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover shadow-md text-sm"
                >
                  {filteredListings.map((opt) => (
                    <li
                      key={opt.id}
                      role="option"
                      aria-selected={opt.id === selectedListingId}
                      data-testid={`add-lead-listing-option-${opt.id}`}
                      className="cursor-pointer px-3 py-2 min-h-[44px] hover:bg-accent hover:text-accent-foreground"
                      onMouseDown={(e) => {
                        e.preventDefault(); // prevent blur before click
                        setListingSearch(opt.name);
                        setSelectedListingId(opt.id);
                        setListingDropdownOpen(false);
                      }}
                    >
                      {opt.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <label htmlFor="add-lead-email" className="block text-sm font-medium mb-1">
              Prospect Email <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <input
              ref={emailInputRef}
              id="add-lead-email"
              data-testid="add-lead-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="prospect@example.com"
              className={inputCls}
              disabled={restDisabled}
              aria-required="true"
            />
          </div>

          <div>
            <label htmlFor="add-lead-phone" className="block text-sm font-medium mb-1">
              Prospect Phone <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="add-lead-phone"
              data-testid="add-lead-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="tel"
              placeholder="+1 (555) 000-0000"
              className={inputCls}
              disabled={restDisabled}
            />
          </div>

          <div>
            <label htmlFor="add-lead-name" className="block text-sm font-medium mb-1">
              Prospect Name <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="add-lead-name"
              data-testid="add-lead-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={inputCls}
              disabled={restDisabled}
            />
          </div>

          <div>
            <label htmlFor="add-lead-source" className="block text-sm font-medium mb-1">
              Source <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="add-lead-source"
              data-testid="add-lead-source-input"
              type="text"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="e.g. walk-in, phone call, referral"
              className={inputCls}
              disabled={restDisabled}
            />
          </div>

          {submitError && (
            <div
              data-testid="add-lead-error"
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {submitError}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <Button
              type="submit"
              data-testid="add-lead-submit-button"
              disabled={isSubmitting || !email.trim() || !effectiveListingId}
              className="flex-1"
            >
              {isSubmitting ? 'Adding…' : 'Add Lead'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="add-lead-cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLeadModal;
